import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Calendar as CalendarIcon, UserCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { TIMEZONES, detectTimezone, formatInTz } from "../../lib/timezones";

/**
 * AdvisorMeetingCard — surfaces the dedicated advisor (Chris Myers) in the
 * Client Portal with a one-click "Book a call" that opens a calendar / timezone
 * picker. Slots come from the same rolling hourly grid the marketing site
 * uses, but filtered by `advisor: "chris"` so his availability is independent
 * from Kalib/John's diagnostic calendar.
 */
export default function AdvisorMeetingCard() {
  const [advisor, setAdvisor] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/portal/advisor")
      .then(({ data }) => setAdvisor(data?.advisor || null))
      .catch(() => { /* silent — surfaces as a disabled state */ });
  }, []);

  if (!advisor) return null;

  return (
    <>
      <div data-testid="advisor-card" className="rounded-2xl border border-[#c9a961]/35 bg-white p-5 flex flex-col gap-4 items-start min-w-0 w-full">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-[#eae7de] border border-[#c9a961]/40 flex items-center justify-center shrink-0">
            <UserCircle2 className="h-8 w-8 text-[#1e3d3a]/70" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Your dedicated advisor</p>
            <p className="mt-1 font-head text-xl text-[#1e3d3a]">{advisor.name}</p>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mt-0.5">{advisor.role}</p>
          </div>
        </div>
        {advisor.bio && (
          <p className="text-sm text-[#1e3d3a]/70 leading-relaxed">{advisor.bio}</p>
        )}
        <button
          type="button"
          data-testid="advisor-book-open"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          Book a call
        </button>
      </div>
      <AdvisorBookingModal
        open={open}
        onClose={() => setOpen(false)}
        advisor={advisor}
      />
    </>
  );
}


function AdvisorBookingModal({ open, onClose, advisor }) {
  const [tz, setTz] = useState(() => detectTimezone());
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayOffset, setDayOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [goal, setGoal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    setConfirmed(null);
    api.get("/portal/advisor/slots")
      .then(({ data }) => setSlots(data?.slots || []))
      .catch(() => toast.error("Could not load Chris's calendar. Please try again."))
      .finally(() => setLoading(false));
  }, [open]);

  // Group slots by their local calendar date so the user can page day by day.
  const byDay = useMemo(() => {
    const acc = {};
    slots.forEach((s) => {
      const key = formatInTz(s.slot_iso, tz, { year: "numeric", month: "2-digit", day: "2-digit" });
      (acc[key] ||= []).push(s);
    });
    return acc;
  }, [slots, tz]);
  const dayKeys = useMemo(() => {
    // Keep only future or today dates in local order.
    return Object.keys(byDay).sort((a, b) => {
      // Parse M/D/YYYY from Intl format — locale-aware but not guaranteed.
      // Fall back to comparing the first slot's ISO.
      const aISO = byDay[a][0].slot_iso;
      const bISO = byDay[b][0].slot_iso;
      return aISO.localeCompare(bISO);
    });
  }, [byDay]);
  const currentDayKey = dayKeys[dayOffset];
  const currentSlots = currentDayKey ? byDay[currentDayKey] : [];

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/advisor/book", {
        slot_iso: selected.slot_iso,
        goal: goal.trim() || null,
        timezone: tz,
      });
      setConfirmed(data);
      toast.success("Booked — check your inbox for the confirmation.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book that slot. Please try another.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1e3d3a]/55 backdrop-blur-sm"
      data-testid="advisor-booking-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-[#c9a961]/25"
      >
        <button
          type="button"
          data-testid="advisor-booking-close"
          onClick={() => !submitting && onClose()}
          disabled={submitting}
          className="absolute top-3 right-3 text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300 p-1"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pt-7 pb-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Book with {advisor.name}</p>
          <h2 className="font-head text-2xl text-[#1e3d3a] tracking-tight mt-1">
            {confirmed ? "You're booked" : "Pick a time that works"}
          </h2>
          {!confirmed && (
            <p className="text-xs text-[#1e3d3a]/55 mt-1">
              All slots shown in your timezone. Chris runs hourly meetings 7am–8pm Eastern.
            </p>
          )}
        </div>

        {confirmed ? (
          <div data-testid="advisor-booking-confirmed" className="px-7 pb-7">
            <div className="rounded-xl border border-[#6b7c3a]/40 bg-[#eae7de]/60 p-5 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">Confirmed</p>
              <p className="mt-2 font-head text-xl text-[#1e3d3a]">{confirmed.pretty}</p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 mt-0.5">{confirmed.timezone}</p>
              <p className="text-xs text-[#1e3d3a]/60 mt-4 leading-relaxed">
                Chris will call you at this time. A confirmation email is on its way.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full rounded-full bg-[#1e3d3a] text-white py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="px-7 pb-3">
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Your timezone</label>
              <select
                data-testid="advisor-tz-select"
                value={tz}
                onChange={(e) => { setTz(e.target.value); setDayOffset(0); setSelected(null); }}
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              >
                {TIMEZONES.map((z) => (
                  <option key={z.value} value={z.value}>{z.label}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="p-12 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
              </div>
            ) : (
              <>
                {/* Day pager */}
                <div className="px-7 py-3 flex items-center justify-between border-y border-[#1e3d3a]/8">
                  <button
                    type="button"
                    data-testid="advisor-day-prev"
                    onClick={() => setDayOffset((o) => Math.max(0, o - 1))}
                    disabled={dayOffset === 0}
                    className="text-[#1e3d3a]/60 hover:text-[#1e3d3a] disabled:opacity-30 transition-colors duration-300 p-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <p data-testid="advisor-day-label" className="font-head text-base text-[#1e3d3a]">
                    {currentDayKey ? new Date(currentSlots[0].slot_iso).toLocaleDateString(undefined, {
                      timeZone: tz, weekday: "long", month: "long", day: "numeric",
                    }) : "No days available"}
                  </p>
                  <button
                    type="button"
                    data-testid="advisor-day-next"
                    onClick={() => setDayOffset((o) => Math.min(dayKeys.length - 1, o + 1))}
                    disabled={dayOffset >= dayKeys.length - 1}
                    className="text-[#1e3d3a]/60 hover:text-[#1e3d3a] disabled:opacity-30 transition-colors duration-300 p-1"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                {/* Slot grid */}
                <div className="px-7 py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {currentSlots.map((s) => {
                    const label = formatInTz(s.slot_iso, tz, { hour: "numeric", minute: "2-digit" });
                    const isSelected = selected?.slot_iso === s.slot_iso;
                    return (
                      <button
                        key={s.slot_iso}
                        type="button"
                        data-testid={`advisor-slot-${s.slot_iso}`}
                        disabled={s.taken}
                        onClick={() => setSelected(s)}
                        className={`text-[11px] py-2 rounded-lg border transition-all duration-300 ${
                          s.taken
                            ? "border-[#1e3d3a]/8 bg-[#eae7de]/40 text-[#1e3d3a]/25 line-through cursor-not-allowed"
                            : isSelected
                            ? "border-[#1e3d3a] bg-[#1e3d3a] text-white font-medium"
                            : "border-[#1e3d3a]/15 bg-white text-[#1e3d3a]/80 hover:border-[#c9a961] hover:text-[#1e3d3a]"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <div className="px-7 pt-1 pb-5">
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">
                    Anything Chris should prep? (optional)
                  </label>
                  <textarea
                    data-testid="advisor-goal-input"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    rows={2}
                    placeholder="e.g. cashflow forecast, pricing changes, hiring plan…"
                    className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                <div className="px-7 pb-6 flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="advisor-book-submit"
                    onClick={submit}
                    disabled={!selected || submitting}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-3 text-sm font-medium tracking-wide hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking…</>
                     : selected ? `Book ${formatInTz(selected.slot_iso, tz, { hour: "numeric", minute: "2-digit", weekday: "short", month: "short", day: "numeric" })}`
                     : "Pick a slot"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] px-4 py-3 transition-colors duration-300 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
